import type { FC } from 'react'
import { useRef } from 'react'

import { AuthClient } from '@skytech/auth'
import { MessagePopup } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { sanitizeAndInsertTargetBlank } from '@skytech/utils/html-sanitization'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import queries from '@/api/queries'
import { LocalDateTime } from '@/components/LocalDateTime'

import { consentResources, type ActiveConsentType } from './api/consent'
import { useAppContext } from './App.context'
import { Translation } from './components/i18n/Translation'

export const EmployeeConsentPopup: FC<{ activeConsent: ActiveConsentType }> = ({
  activeConsent,
}) => {
  const context = useAppContext()
  const queryClient = useQueryClient()
  const agreeButtonRef = useRef<HTMLButtonElement | null>(null)

  const acceptConsentMutation = useMutation(consentResources.acceptConsent, {
    onSuccess() {
      context.setIsActiveEmployeeConsentModalOpen(false)
      queryClient.invalidateQueries(queries.activeConsent())
    },
  })

  const revokeConsentMutation = useMutation(consentResources.revokeConsent, {
    onSuccess() {
      AuthClient.logOut()
    },
  })

  const onCloseHandler = () => {
    if (!activeConsent || !activeConsent.isConsentGiven) return
    context.setIsActiveEmployeeConsentModalOpen(false)
  }

  const onSecondaryButtonClick = () => {
    if (!activeConsent || !activeConsent.isConsentGiven) {
      AuthClient.logOut()
    } else {
      context.setIsActiveEmployeeConsentModalOpen(false)
    }
  }

  const onPrimaryButtonClick = () => {
    if (!activeConsent || !activeConsent.isConsentGiven) {
      acceptConsentMutation.mutate(activeConsent.id)
    } else {
      revokeConsentMutation.mutate(activeConsent.id)
    }
  }

  if (!activeConsent) return null

  return (
    <MessagePopup
      // initialFocusRef={agreeButtonRef}
      type="warning"
      className="employee-consent-popup"
      message={
        <span
          dangerouslySetInnerHTML={{ __html: sanitizeAndInsertTargetBlank(activeConsent.text) }}
        />
      }
      title={<Translation id="Employee consent" />}
      isOpen={context.isActiveEmployeeConsentModalOpen}
      onClose={onCloseHandler}
    >
      <footer className="flex w-full flex-row items-center justify-between">
        <div>
          {activeConsent.isConsentGiven ? (
            <p>
              <Translation id="Consent given: " />
              <LocalDateTime date={activeConsent.datetime} />
            </p>
          ) : (
            ''
          )}
        </div>
        <div>
          <Button className="px-2" variant="tertiary" onClick={onSecondaryButtonClick}>
            {activeConsent.isConsentGiven ? (
              <Translation id="Close" />
            ) : (
              <Translation id="Disagree" />
            )}
          </Button>

          <Button
            className="capitalize"
            ref={agreeButtonRef}
            variant="primary"
            isLoading={acceptConsentMutation.isLoading}
            onClick={onPrimaryButtonClick}
          >
            {activeConsent.isConsentGiven ? (
              <Translation id="Revoke" />
            ) : (
              <Translation id="Agree" />
            )}
          </Button>
        </div>
      </footer>
    </MessagePopup>
  )
}
